import { useState, useCallback, useRef } from 'react';
import { AnnotationRect } from '../types';

export const useAnnotationState = () => {
  // Annotation mode state
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [annotations, setAnnotations] = useState<AnnotationRect[]>([]);
  const [currentAnnotation, setCurrentAnnotation] = useState<AnnotationRect | null>(null);
  const [isDrawingAnnotation, setIsDrawingAnnotation] = useState(false);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [annotationStyle, setAnnotationStyle] = useState<'fill' | 'outline'>('fill');

  // Secondary annotation state (for dual view)
  const [secondaryAnnotations, setSecondaryAnnotations] = useState<AnnotationRect[]>([]);
  const [currentSecondaryAnnotation, setCurrentSecondaryAnnotation] = useState<AnnotationRect | null>(null);
  const [isDrawingSecondaryAnnotation, setIsDrawingSecondaryAnnotation] = useState(false);
  const [selectedSecondaryAnnotationId, setSelectedSecondaryAnnotationId] = useState<string | null>(null);

  // Use refs to track deletion state to prevent React 18 batching issues
  const deletionInProgressRef = useRef(false);

  const deleteAnnotation = useCallback((id: string) => {
    if (deletionInProgressRef.current) return;
    deletionInProgressRef.current = true;

    // Use setTimeout to ensure React 18 batching doesn't interfere
    setTimeout(() => {
      setAnnotations(prev => prev.filter(annotation => annotation.id !== id));
      setSelectedAnnotationId(prev => prev === id ? null : prev);
      deletionInProgressRef.current = false;
    }, 0);
  }, []);

  const deleteSecondaryAnnotation = useCallback((id: string) => {
    if (deletionInProgressRef.current) return;
    deletionInProgressRef.current = true;

    setTimeout(() => {
      setSecondaryAnnotations(prev => prev.filter(annotation => annotation.id !== id));
      setSelectedSecondaryAnnotationId(prev => prev === id ? null : prev);
      deletionInProgressRef.current = false;
    }, 0);
  }, []);

  const clearAllAnnotations = useCallback(() => {
    if (deletionInProgressRef.current) return;
    deletionInProgressRef.current = true;

    // Clear all state in a single batch to prevent flickering
    setTimeout(() => {
      setAnnotations([]);
      setSecondaryAnnotations([]);
      setCurrentAnnotation(null);
      setCurrentSecondaryAnnotation(null);
      setSelectedAnnotationId(null);
      setSelectedSecondaryAnnotationId(null);
      setIsDrawingAnnotation(false);
      setIsDrawingSecondaryAnnotation(false);
      setIsAnnotationMode(false);
      deletionInProgressRef.current = false;
    }, 0);
  }, []);

  return {
    isAnnotationMode,
    setIsAnnotationMode,
    annotations,
    setAnnotations,
    currentAnnotation,
    setCurrentAnnotation,
    isDrawingAnnotation,
    setIsDrawingAnnotation,
    selectedAnnotationId,
    setSelectedAnnotationId,
    annotationStyle,
    setAnnotationStyle,
    
    // Secondary annotations
    secondaryAnnotations,
    setSecondaryAnnotations,
    currentSecondaryAnnotation,
    setCurrentSecondaryAnnotation,
    isDrawingSecondaryAnnotation,
    setIsDrawingSecondaryAnnotation,
    selectedSecondaryAnnotationId,
    setSelectedSecondaryAnnotationId,

    // Utility functions
    deleteAnnotation,
    deleteSecondaryAnnotation,
    clearAllAnnotations
  };
};